import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/authContextCore';
import {
  getCart, removeCartLine, updateCartLine,
  openCheckout, optimizeImageUrl, setCartUser, clearCartForLogout, clearAllCartLines,
  type StoreProduct, type Cart,
} from '@/services/shopifyStorefront';
import { type ProductHeaderStyle } from '../../../shared/pageBlocks';
import { sanitizeRichHtml } from '../../../shared/newsletterBlocks';
import { LibraryMediaView } from '@/features/site/components/LibraryMediaView';
import { galleryMediaKind, GALLERY_KIND_GLYPH, type GalleryMediaItem } from '@/features/store/galleryMedia';
import { thumbUrlFromMediaSrc } from '@/utils/fileUtils';
import { useProgressiveTierSrc, useProgressiveTierVideoRef } from '@/features/site/useProgressiveTierSrc';
import { coverViewportPx } from '@/utils/abrQuality';
import { pageMayTakeKey } from '@/utils/textEntry';

// Exported leaf helpers/components below are reused by the public site builder
// pages (src/features/store) so storefront UI stays DRY across chat + /home.
export function stripHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export function formatPrice(price: { amount: string; currencyCode: string }): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currencyCode,
  }).format(parseFloat(price.amount));
}

export function isDigital(product: StoreProduct): boolean {
  const type = product.productType.toLowerCase();
  const title = product.title.toLowerCase();
  return type.includes('pack') || type.includes('video') || type.includes('digital') || title.includes('pack');
}

function isVideoPack(product: StoreProduct): boolean {
  return product.productType.toLowerCase() === 'video pack';
}

export function shouldShow(product: StoreProduct): boolean {
  if (isVideoPack(product)) return false;
  if (isDigital(product)) return true;
  return product.availableForSale;
}

// A card-carousel slide: an image, or a video (played inline, muted+looped). 3D/
// pdf/other items appear via their poster image when one is set.
export interface CardMedia { src: string; altText: string | null; kind: 'image' | 'video'; poster?: string | null; variantPlaceholderSrc?: string | null; variantSrc?: string | null; variantLgSrc?: string | null; videoVariantSrc?: string | null }

// Gallery-strip thumbnail: `thumbUrlFromMediaSrc` builds a `/thumb` URL
// unconditionally whenever `src` contains a media UUID — it doesn't check
// that a thumbnail actually EXISTS server-side, so a `||` fallback chain
// never engages on a 404 (only when no UUID is present at all). An item
// whose classic 96px thumb was never generated 404s with no recovery. This
// uses a real onError fallback chain: classic thumb → lightweight variant →
// optimizeImageUrl (Shopify only) → the original, so a stale/missing thumb
// degrades gracefully instead of showing a broken-image icon — and never
// falls all the way through to the raw multi-MB original for a 56px box
// without at least SHOWING something first: a neutral skeleton fills the
// box (instead of nothing) until whichever candidate lands, then fades in.
const GalleryStripThumb: React.FC<{ src: string; variantSrc?: string | null; alt: string; className?: string }> = ({ src, variantSrc, alt, className }) => {
  const candidates = useMemo(() => {
    const list = [thumbUrlFromMediaSrc(src), variantSrc || undefined, optimizeImageUrl(src, 100), src].filter(
      (u): u is string => !!u,
    );
    return Array.from(new Set(list));
  }, [src, variantSrc]);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setIdx(0); setLoaded(false); }, [candidates]);
  return (
    <div className={className} style={{ position: 'relative', overflow: 'hidden', background: '#27272a' }}>
      <img
        src={candidates[idx]}
        alt={alt}
        className="w-full h-full object-cover"
        style={{ opacity: loaded ? 1 : 0, transition: 'opacity 150ms ease' }}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => { setLoaded(false); setIdx((i) => Math.min(i + 1, candidates.length - 1)); }}
      />
    </div>
  );
};

// Flatten a product's merged gallery (the assets the user set on its detail page)
// into card slides, preserving video items so they actually play in the carousel.
// `showVideos` (default true) is the per-block toggle: when off, a video shows its
// poster as a still (or is skipped if it has none). Shared by both card components.
//
// A DEFINED gallery is authoritative — it already reflects the user's settings
// (custom order + `showShopifyImages:false`), so we render exactly it (never re-add
// Shopify). Only an UNDEFINED gallery (no customization) falls back to Shopify.
export function cardImagesFromGallery(
  gallery: { src: string; altText?: string | null; mime?: string; poster?: string | null; variantPlaceholderSrc?: string | null; variantSrc?: string | null; variantLgSrc?: string | null; videoVariantSrc?: string | null }[] | undefined,
  fallback: { src: string; altText: string | null }[],
  showVideos = true,
): CardMedia[] {
  if (!gallery) return fallback.map((f) => ({ src: f.src, altText: f.altText, kind: 'image' as const }));
  const out: CardMedia[] = [];
  for (const g of gallery) {
    const mime = g.mime || '';
    const altText = g.altText ?? null;
    if (!mime || mime.startsWith('image/')) { out.push({ src: g.src, altText, kind: 'image', variantPlaceholderSrc: g.variantPlaceholderSrc, variantSrc: g.variantSrc, variantLgSrc: g.variantLgSrc }); continue; }
    if (mime.startsWith('video/')) {
      if (showVideos) out.push({ src: g.src, altText, kind: 'video', poster: g.poster ?? null, videoVariantSrc: g.videoVariantSrc });
      else if (g.poster) out.push({ src: g.poster, altText, kind: 'image' });
      continue;
    }
    // 3D / pdf / other — only representable as a still, via its poster.
    if (g.poster) out.push({ src: g.poster, altText, kind: 'image' });
  }
  return out;
}

// Render one card slide: a staged small→large/original <img>, or a seamless
// small→original <video> that never reloads/stalls mid-play (see
// useProgressiveTierSrc / useProgressiveTierVideoRef). `optimizeImageUrl`
// (Shopify-CDN-URL transform, no-ops for our own media) is the ceiling for
// Shopify-sourced images that never carry a generated variant.
//
// Callers MUST key this by the item's identity (`item.src`) — see
// ProductCard/MobileFullScreenProductCard. The prev/current/next slide
// SLOTS are fixed, non-remounting positions that get handed a different
// `item` every time the gallery advances; without a key, the video branch
// would keep the SAME <video> element (and its already-fully-loaded
// PREVIOUS item's frame) visible for a tick before the ref effect catches up
// and swaps `.src` — a visible flash of the wrong video. A key forces a
// clean remount instead, so a new item never has anything stale to show.
const CardSlide: React.FC<{ item: CardMedia; size: number; title: string; className?: string; style?: React.CSSProperties }> = ({ item, size, title, className, style }) => {
  // Plain Shopify-sourced items (no custom gallery on this product — see
  // cardImagesFromGallery's `!gallery` fallback) never carry OUR variant
  // fields at all, which used to hit useProgressiveTierSrc's "nothing staged"
  // escape hatch: original shown immediately, UNPRELOADED — exactly the
  // "high-quality asset streams in top-to-bottom" bug, just for the Shopify-
  // image case specifically (custom-gallery items generate real placeholder/
  // small/large tiers and were never affected). Shopify's own CDN already
  // supports on-the-fly resizing via optimizeImageUrl, so a genuinely tiny
  // (24px) placeholder costs nothing extra to request and is enough to keep
  // the card-sized `original` on the properly-gated (preloaded-before-shown)
  // path instead of the unstaged one.
  // optimizeImageUrl no-ops (returns `src` unchanged) for our OWN media URLs
  // (no `.ext` for its width-suffix regex to match) — guard against using
  // that no-op result as a "placeholder", which would just be the full item
  // itself, undoing the whole point for our media that hasn't generated a
  // real placeholder yet.
  const tinyShopifyPlaceholder = item.kind === 'image' ? optimizeImageUrl(item.src, 24) : item.src;
  const placeholder = item.variantPlaceholderSrc
    ?? (tinyShopifyPlaceholder !== item.src ? tinyShopifyPlaceholder : undefined);
  // Both hooks called unconditionally (item.kind can change across renders
  // of the same mounted slide slot) — the unused one is a cheap no-op since
  // its `small` is always undefined for the other kind.
  const imageSrc = useProgressiveTierSrc({
    placeholder, small: item.variantSrc, large: item.variantLgSrc,
    original: optimizeImageUrl(item.src, size),
    // The card box is `size` CSS px wide — the auto-quality display cap
    // (abrQuality.ts) keeps a ~300px card from ever fetching large/original.
    displayWidthPx: size,
  });
  const videoRef = useProgressiveTierVideoRef({ small: item.videoVariantSrc, original: item.src, displayWidthPx: size });

  if (item.kind === 'video') {
    return (
      <video ref={videoRef}
        poster={item.poster || thumbUrlFromMediaSrc(item.src)} muted loop playsInline autoPlay preload="metadata"
        draggable={false} className={className} style={style} />
    );
  }
  return <img src={imageSrc} alt={item.altText || title} className={className} style={style} loading="lazy" draggable={false} />;
};

// =====================
// Cart Dropdown
// =====================
const XIcon: React.FC<{ className?: string }> = ({ className = 'w-3 h-3' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const CartDropdown: React.FC<{
  cart: Cart;
  onRemove: (lineId: string) => void;
  onClearAll: () => void;
  onUpdateQty: (lineId: string, qty: number) => void;
  onCheckout: () => void;
  removing: string | null;
  clearing: boolean;
  rightOffset?: number;
}> = ({ cart, onRemove, onClearAll, onUpdateQty, onCheckout, removing, clearing, rightOffset = 0 }) => (
  <div className="absolute top-full mt-2 w-72 bg-zinc-900 border border-white/20 rounded-xl shadow-2xl overflow-hidden z-[100]" style={{ right: rightOffset }}>
    <div className="flex items-center justify-between p-3 border-b border-white/10">
      <span className="text-sm font-medium text-white">Cart ({cart.totalQuantity})</span>
      <button
        onClick={onClearAll}
        disabled={clearing}
        className="flex items-center justify-center w-6 h-6 rounded-md bg-red-500/20 text-red-400 hover:bg-red-500/40 hover:text-red-300 transition-colors"
        title="Clear all items"
      >
        {clearing ? (
          <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" />
        ) : (
          <XIcon />
        )}
      </button>
    </div>
    <div className="max-h-64 overflow-y-auto">
      {cart.lines.map((line) => (
        <div key={line.id} className="flex items-center gap-2 p-2 border-b border-white/5">
          {line.image && (
            <img src={optimizeImageUrl(line.image.src, 60)} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs text-white truncate">{line.productTitle}</div>
            {line.variantTitle !== 'Default Title' && (
              <div className="text-[10px] text-zinc-400 truncate">{line.variantTitle}</div>
            )}
            <div className="text-xs text-cyan-400">{formatPrice(line.price)}</div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => line.quantity > 1 ? onUpdateQty(line.id, line.quantity - 1) : onRemove(line.id)}
              className="w-5 h-5 flex items-center justify-center rounded bg-white/10 text-zinc-300 hover:bg-white/20 text-xs"
            >-</button>
            <span className="text-xs text-white w-4 text-center">{line.quantity}</span>
            <button
              onClick={() => onUpdateQty(line.id, line.quantity + 1)}
              className="w-5 h-5 flex items-center justify-center rounded bg-white/10 text-zinc-300 hover:bg-white/20 text-xs"
            >+</button>
          </div>
          <button
            onClick={() => onRemove(line.id)}
            disabled={removing === line.id}
            className="flex items-center justify-center w-6 h-6 rounded-md bg-red-500/20 text-red-400 hover:bg-red-500/40 hover:text-red-300 transition-colors flex-shrink-0"
          >
            {removing === line.id ? (
              <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <XIcon />
            )}
          </button>
        </div>
      ))}
    </div>
    <div className="p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-400">Total</span>
        <span className="text-sm font-semibold text-white">{formatPrice(cart.totalAmount)}</span>
      </div>
      <button
        onClick={onCheckout}
        className="w-full py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold transition-colors"
      >
        Checkout
      </button>
    </div>
  </div>
);

// =====================
// Cart Pill Button
// =====================
/** The dropdown's width (w-72) and the margin it keeps from the viewport's left edge. */
const CART_DROPDOWN_WIDTH = 288;
const CART_DROPDOWN_MARGIN = 8;
// Frames a not-yet-placed pill is measured again.
const CART_PLACE_RETRIES = 3;

export interface CartPillState {
  /** The cart (null until loaded, or empty after checkout / clearing). */
  cart: Cart | null;
  /** Items in it (the pill hides at 0). */
  count: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  /** The pill's press: opens (sized to stay on screen) or closes the dropdown. */
  toggle: () => void;
  /** The element the dropdown hangs from (the pill's container; the dropdown's clicks are "inside" it). */
  ref: React.RefObject<HTMLDivElement>;
  /** The dropdown while open (absolute, under `ref`'s element); null closed. */
  dropdown: React.ReactNode;
}

/**
 * CartPill's state and behaviour, for the DOM pill below and for the header
 * sleepy-ui draws (which hangs the dropdown from an element at its pill).
 */
export function useCartPill(): CartPillState {
  const { user } = useAuth();
  const [cart, setCart] = useState<Cart | null>(null);
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [dropdownRight, setDropdownRight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  // right-0 aligns the dropdown's right edge with the pill's; if that would
  // push it off the left edge, shift it right.
  // An element not laid out yet (the pill drawn by sleepy-ui is placed a
  // frame after it first shows) is measured again on the next frames.
  const placeDropdown = useCallback(function place(retries = CART_PLACE_RETRIES) {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    if (rect.width === 0 && retries > 0) {
      setDropdownRight(0);
      requestAnimationFrame(() => place(retries - 1));
      return;
    }
    const leftEdge = rect.right - CART_DROPDOWN_WIDTH;
    setDropdownRight(leftEdge < CART_DROPDOWN_MARGIN ? leftEdge - CART_DROPDOWN_MARGIN : 0);
  }, []);

  // Sync cart user on login/logout
  const prevUserRef = useRef<typeof user>(undefined);
  useEffect(() => {
    const wasLoggedIn = prevUserRef.current !== undefined && prevUserRef.current != null;
    prevUserRef.current = user;

    if (user) {
      setCartUser(String(user.id));
      getCart().then(setCart).catch(() => {});
    } else if (wasLoggedIn) {
      // Only clear on actual logout, not on initial mount as anonymous
      clearCartForLogout();
      setCart(null);
    } else {
      // Anonymous user (initial mount or remount) — load their cart
      setCartUser(null);
      getCart().then(setCart).catch(() => {});
    }
  }, [user]);

  // Listen for cart updates — auto-open dropdown when item is added
  useEffect(() => {
    const handler = (e: Event) => {
      const updated = (e as CustomEvent<Cart>).detail;
      setCart((prev) => {
        if (updated && (!prev || updated.totalQuantity > prev.totalQuantity)) {
          placeDropdown();
          setOpen(true);
        }
        return updated;
      });
    };
    window.addEventListener('cart-updated', handler);
    return () => window.removeEventListener('cart-updated', handler);
  }, [placeDropdown]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    // pointerdown, not mousedown: a touch tap (on a drawn sleepy-ui surface's canvas too) makes no mousedown.
    const handler = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  const handleRemove = async (lineId: string) => {
    setRemoving(lineId);
    try {
      const updated = await removeCartLine(lineId);
      setCart(updated);
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: updated }));
    } catch (e) { console.error('Remove failed:', e); }
    finally { setRemoving(null); }
  };

  const handleUpdateQty = async (lineId: string, qty: number) => {
    try {
      const updated = await updateCartLine(lineId, qty);
      setCart(updated);
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: updated }));
    } catch (e) { console.error('Update failed:', e); }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      await clearAllCartLines();
      setCart(null);
      setOpen(false);
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: null }));
    } catch (e) { console.error('Clear all failed:', e); }
    finally { setClearing(false); }
  };

  const handleCheckout = () => {
    if (cart) openCheckout(cart);
  };

  const toggle = () => {
    if (!open) placeDropdown();
    setOpen(!open);
  };

  const dropdown = open && cart ? (
    <CartDropdown
      cart={cart}
      onRemove={handleRemove}
      onClearAll={handleClearAll}
      onUpdateQty={handleUpdateQty}
      onCheckout={handleCheckout}
      removing={removing}
      clearing={clearing}
      rightOffset={dropdownRight}
    />
  ) : null;

  return { cart, count: cart?.totalQuantity ?? 0, open, setOpen, toggle, ref, dropdown };
}

export const CartPill: React.FC<{ className?: string }> = ({ className = '' }) => {
  const pill = useCartPill();
  if (!pill.cart || pill.count === 0) return null;

  return (
    <div ref={pill.ref} className={`relative ${className}`}>
      <button
        onClick={pill.toggle}
        className="p-2 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 transition-colors flex items-center gap-1.5"
      >
        <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </svg>
        <span className="text-xs font-semibold text-white">{pill.count}</span>
      </button>
      {pill.dropdown}
    </div>
  );
};

// =====================
// Mobile Full-Screen Product Card (YouTube Shorts-style)
// =====================
export const MobileFullScreenProductCard: React.FC<{
  product: StoreProduct;
  onLearnMore: () => void;
  onAddToCart: (variantId: string) => Promise<void>;
  /** Canonical merged gallery from the product's detail page (same assets). */
  gallery?: { src: string; altText?: string | null; mime?: string; poster?: string | null; variantSrc?: string | null; variantLgSrc?: string | null; videoVariantSrc?: string | null }[];
  /** Show video gallery items as inline players (default true). */
  showVideos?: boolean;
}> = ({ product, onLearnMore, onAddToCart, gallery, showVideos = true }) => {
  const firstVariant = product.variants[0];
  const desc = stripHtml(product.descriptionHtml);
  const canBuy = firstVariant?.availableForSale || isDigital(product);
  const images = React.useMemo(() => cardImagesFromGallery(gallery, product.images, showVideos), [gallery, product.images, showVideos]);
  const hasMultipleImages = images.length > 1;

  const [adding, setAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);

  // IG-style horizontal swipe state
  const touchRef = useRef<{ startX: number; startY: number; locked: 'h' | 'v' | null; dragging: boolean; currentDx: number }>({ startX: 0, startY: 0, locked: null, dragging: false, currentDx: 0 });
  const [dragX, setDragX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  // 'sliding' is a % offset applied with transition for animated slide (wheel/programmatic)
  const [slideOffset, setSlideOffset] = useState(0);
  const [noTransition, setNoTransition] = useState(false);
  const slidingRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const swipeZoneRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const autoPlayPausedRef = useRef(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Non-passive touch listeners for iOS Safari preventDefault
  useEffect(() => {
    const el = swipeZoneRef.current;
    if (!el || !hasMultipleImages) return;

    const onTouchStart = (e: TouchEvent) => {
      // If a slide animation is in flight, let it finish before accepting new touches
      if (slidingRef.current) return;
      const t = e.touches[0];
      touchRef.current = { startX: t.clientX, startY: t.clientY, locked: null, dragging: true, currentDx: 0 };
      autoPlayPausedRef.current = true;
      setSwiping(true);
      setDragX(0);
    };

    const onTouchMove = (e: TouchEvent) => {
      const state = touchRef.current;
      if (!state.dragging) return;
      const t = e.touches[0];
      const dx = t.clientX - state.startX;
      const dy = t.clientY - state.startY;

      if (!state.locked) {
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          state.locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        } else return;
      }

      if (state.locked === 'h') {
        e.preventDefault();
        state.currentDx = dx;
        setDragX(dx);
      }
      // vertical lock: do nothing, let native scroll-snap handle it
    };

    const onTouchEnd = () => {
      const state = touchRef.current;
      const wasHorizontal = state.locked === 'h';
      const dx = state.currentDx;
      state.dragging = false;
      state.locked = null;
      state.currentDx = 0;

      if (wasHorizontal && Math.abs(dx) > 50) {
        // Successful swipe — animate out in px, then swap index
        const dir = dx < 0 ? 1 : -1;
        const w = el.offsetWidth || window.innerWidth;
        slidingRef.current = true;
        // Keep swiping mode but animate dragX to full width (CSS transition via swiping=false)
        setSwiping(false);
        setDragX(dir === -1 ? w : -w);
        setTimeout(() => {
          setNoTransition(true);
          setDragX(0);
          setImgIdx((prev) => (prev + dir + images.length) % images.length);
          requestAnimationFrame(() => {
            setNoTransition(false);
            slidingRef.current = false;
          });
        }, 300);
      } else {
        // Not enough distance or vertical — snap back
        setSwiping(false);
        setDragX(0);
      }
      // Resume auto-advance after 5s of no interaction
      setTimeout(() => { autoPlayPausedRef.current = false; }, 5000);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [hasMultipleImages, images.length]);

  // Desktop: horizontal wheel/trackpad scroll to switch images (with slide animation)
  // Requires strongly horizontal gesture (deltaX > 3× deltaY) to avoid false triggers from vertical scrolling.
  // One gesture = one image advance, then cooldown until 400ms of silence.
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !hasMultipleImages) return;
    let fired = false;
    let cooldown: ReturnType<typeof setTimeout>;

    const onWheel = (e: WheelEvent) => {
      const ax = Math.abs(e.deltaX);
      const ay = Math.abs(e.deltaY);
      // Only treat as horizontal if deltaX strongly dominates
      if (ax < 3 * ay || ax < 10) return;
      e.preventDefault();
      if (fired || slidingRef.current) {
        clearTimeout(cooldown);
        cooldown = setTimeout(() => { fired = false; }, 400);
        return;
      }
      const dir = e.deltaX > 0 ? 1 : -1;
      fired = true;
      autoPlayPausedRef.current = true;
      slidingRef.current = true;
      // Phase 1: animate slide to ±100%
      setSlideOffset(dir === 1 ? -100 : 100);
      setTimeout(() => {
        // Phase 2: disable transition, swap index + snap back to 0 in one frame
        setNoTransition(true);
        setImgIdx((prev) => (prev + dir + images.length) % images.length);
        setSlideOffset(0);
        // Phase 3: re-enable transition next frame
        requestAnimationFrame(() => {
          setNoTransition(false);
          slidingRef.current = false;
        });
      }, 300);
      // Resume auto-advance after 5s of no interaction
      cooldown = setTimeout(() => { fired = false; autoPlayPausedRef.current = false; }, 5000);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [hasMultipleImages, images.length]);

  // Track visibility via IntersectionObserver
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.intersectionRatio > 0.6;
        setIsVisible(visible);
        if (visible) {
          // Card is active — cancel any pending reset
          clearTimeout(resetTimerRef.current);
        } else {
          // Card left view — wait for scroll to settle, then silently reset to first image
          clearTimeout(resetTimerRef.current);
          resetTimerRef.current = setTimeout(() => {
            // Wait for any in-flight slide animation to finish
            const waitForSlide = () => {
              if (slidingRef.current) {
                setTimeout(waitForSlide, 100);
                return;
              }
              setTimeout(() => {
                setNoTransition(true);
                setImgIdx(0);
                setSlideOffset(0);
                requestAnimationFrame(() => setNoTransition(false));
              }, 300);
            };
            waitForSlide();
          }, 500);
        }
      },
      { threshold: [0, 0.6] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Auto-advance images every 3s when visible and not interacting
  useEffect(() => {
    if (!isVisible || !hasMultipleImages) return;
    const interval = setInterval(() => {
      if (slidingRef.current || autoPlayPausedRef.current) return;
      slidingRef.current = true;
      setSlideOffset(-100);
      setTimeout(() => {
        setNoTransition(true);
        setImgIdx((prev) => (prev + 1) % images.length);
        setSlideOffset(0);
        requestAnimationFrame(() => {
          setNoTransition(false);
          slidingRef.current = false;
        });
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [isVisible, hasMultipleImages, images.length]);

  const handleAdd = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!firstVariant?.availableForSale || adding) return;
    setAdding(true);
    try {
      await onAddToCart(firstVariant.id);
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1500);
    } finally {
      setAdding(false);
    }
  };

  const prevImgIdx = (imgIdx - 1 + images.length) % images.length;
  const nextImgIdx = (imgIdx + 1) % images.length;

  return (
    <div
      ref={cardRef}
      className="relative w-full bg-black flex-shrink-0"
      style={{ height: 'calc(var(--app-height, 100dvh) + var(--island-runway, 0px))', scrollSnapAlign: 'start', scrollSnapStop: 'always' }}
    >
      {/* Full-screen image background with IG-style horizontal swipe */}
      <div className="absolute inset-0 overflow-hidden">
        {images.length > 0 ? (
          <div
            ref={swipeZoneRef}
            className="absolute inset-0"
            style={{ touchAction: hasMultipleImages ? 'pan-y' : undefined }}
          >
            <div
              className="absolute inset-0"
              style={{
                transform: hasMultipleImages
                  ? dragX !== 0
                    ? `translateX(${dragX}px)`
                    : slideOffset !== 0
                    ? `translateX(${slideOffset}%)`
                    : 'translateX(0)'
                  : 'none',
                transition: swiping || noTransition ? 'none' : 'transform 0.3s ease-out',
              }}
            >
              {/* Each slide keyed by `slot:src` — the slot name alone keeps prev/
                  current/next from ever colliding (a 2-image gallery has
                  prevImgIdx === nextImgIdx, so `src` alone isn't unique across
                  siblings), and the src half still forces a remount whenever
                  the item THAT slot shows actually changes. See CardSlide's
                  own comment for why a remount (not just a prop update) is
                  required here. */}
              {hasMultipleImages && (
                <CardSlide key={`prev:${images[prevImgIdx].src}`} item={images[prevImgIdx]} size={800} title={product.title}
                  className="absolute top-0 h-full w-full object-cover" style={{ left: '-100%' }} />
              )}
              <CardSlide key={`cur:${images[imgIdx].src}`} item={images[imgIdx]} size={800} title={product.title}
                className="absolute top-0 left-0 h-full w-full object-cover" />
              {hasMultipleImages && (
                <CardSlide key={`next:${images[nextImgIdx].src}`} item={images[nextImgIdx]} size={800} title={product.title}
                  className="absolute top-0 h-full w-full object-cover" style={{ left: '100%' }} />
              )}
            </div>
          </div>
        ) : (
          <div className="w-full h-full bg-zinc-900 flex items-center justify-center text-zinc-600">
            No image
          </div>
        )}
      </div>

      {/* Sold out badge */}
      {!canBuy && (
        <div className="absolute top-4 right-4 z-20 bg-red-600/90 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          Sold Out
        </div>
      )}

      {/* Bottom gradient overlay with product info */}
      <div
        className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent pt-20 px-4"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))' }}
      >
        <div className="text-lg font-semibold text-white mb-1 drop-shadow-lg">{product.title}</div>

        {firstVariant && (
          <div className="flex items-center gap-2 mb-1">
            <span className="text-cyan-400 font-bold text-base drop-shadow-lg">
              {formatPrice(firstVariant.price)}
            </span>
            {firstVariant.compareAtPrice && parseFloat(firstVariant.compareAtPrice.amount) > parseFloat(firstVariant.price.amount) && (
              <span className="text-zinc-400 text-sm line-through drop-shadow-lg">
                {formatPrice(firstVariant.compareAtPrice)}
              </span>
            )}
          </div>
        )}

        {desc && (
          <div className="text-zinc-300 text-sm line-clamp-2 mb-3 drop-shadow-lg">
            {desc}
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={handleAdd}
            disabled={adding || !canBuy}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
              !canBuy
                ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                : justAdded
                ? 'bg-green-600 text-white'
                : adding
                ? 'bg-cyan-700 text-cyan-200 cursor-wait'
                : 'bg-cyan-600 hover:bg-cyan-500 text-white'
            }`}
            style={{ touchAction: 'manipulation' }}
          >
            {adding ? (
              <div className="w-4 h-4 border-2 border-cyan-200 border-t-transparent rounded-full animate-spin" />
            ) : justAdded ? (
              <>
                Added
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </>
            ) : !canBuy ? 'Sold Out' : (
              <>
                Add to Cart
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
              </>
            )}
          </button>
          <button
            onClick={onLearnMore}
            className="px-5 py-2.5 rounded-full text-sm font-medium bg-white/15 hover:bg-white/25 text-white transition-colors"
            style={{ touchAction: 'manipulation' }}
          >
            Learn More
          </button>
        </div>
      </div>

      {/* Image indicator dots — below everything */}
      {hasMultipleImages && (
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1.5 z-20"
          style={{ bottom: 'max(0.75rem, env(safe-area-inset-bottom, 0.75rem))' }}
        >
          {images.map((_, i) => (
            <div
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${
                i === imgIdx ? 'bg-white' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// =====================
// Product Card
// =====================
export const ProductCard: React.FC<{
  product: StoreProduct;
  onClick: () => void;
  onAddToCart: (variantId: string) => Promise<void>;
  isMobile?: boolean;
  /** Canonical merged gallery (Shopify + the user's own assets, in their order)
   *  from the product's detail page. When set, the card slides over THESE assets
   *  so it matches the detail gallery; falls back to raw Shopify images. */
  gallery?: { src: string; altText?: string | null; mime?: string; poster?: string | null; variantSrc?: string | null; variantLgSrc?: string | null; videoVariantSrc?: string | null }[];
  /** Show video gallery items as inline players (default true). */
  showVideos?: boolean;
}> = ({ product, onClick, onAddToCart, isMobile: _isMobile = false, gallery, showVideos = true }) => {
  const firstVariant = product.variants[0];
  const desc = stripHtml(product.descriptionHtml);
  const [adding, setAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const images = React.useMemo(() => cardImagesFromGallery(gallery, product.images, showVideos), [gallery, product.images, showVideos]);
  const hasMultipleImages = images.length > 1;

  const handleAdd = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!firstVariant?.availableForSale || adding) return;
    setAdding(true);
    try {
      await onAddToCart(firstVariant.id);
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1500);
    } finally {
      setAdding(false);
    }
  };

  const canBuy = firstVariant?.availableForSale || isDigital(product);
  const [offset, setOffset] = useState(0);
  const animating = useRef(false);

  const goImage = (dir: 'left' | 'right', e: React.MouseEvent) => {
    e.stopPropagation();
    if (animating.current) return;
    animating.current = true;
    // Kick off the slide
    setOffset(dir === 'right' ? -1 : 1);
    setTimeout(() => {
      // After transition ends: update index, snap back instantly
      animating.current = false;
      setImgIdx(dir === 'right'
        ? (imgIdx + 1) % images.length
        : (imgIdx - 1 + images.length) % images.length
      );
      setOffset(0);
    }, 300);
  };

  const prevImgIdx = (imgIdx - 1 + images.length) % images.length;
  const nextImgIdx = (imgIdx + 1) % images.length;

  return (
    <div className="bg-zinc-900/80 border border-white/10 rounded-lg overflow-hidden hover:border-white/20 transition-colors relative">
      <div className="relative cursor-pointer overflow-hidden" onClick={onClick}>
        <div className="aspect-[4/3] relative">
          {images.length > 0 ? (
            <div
              className="absolute inset-0"
              style={{
                transform: hasMultipleImages ? `translateX(${offset * 100}%)` : 'none',
                transition: offset !== 0 ? 'transform 0.3s ease-out' : 'none',
              }}
            >
              {hasMultipleImages && (
                <CardSlide key={`prev:${images[prevImgIdx].src}`} item={images[prevImgIdx]} size={400} title={product.title}
                  className="absolute top-0 h-full w-full object-cover" style={{ left: '-100%' }} />
              )}
              <CardSlide key={`cur:${images[imgIdx].src}`} item={images[imgIdx]} size={400} title={product.title}
                className="absolute top-0 left-0 h-full w-full object-cover" />
              {hasMultipleImages && (
                <CardSlide key={`next:${images[nextImgIdx].src}`} item={images[nextImgIdx]} size={400} title={product.title}
                  className="absolute top-0 h-full w-full object-cover" style={{ left: '100%' }} />
              )}
            </div>
          ) : (
            <div className="w-full h-full bg-zinc-800 flex items-center justify-center text-zinc-600">
              No image
            </div>
          )}
        </div>
        {hasMultipleImages && (
          <>
            <button
              onClick={(e) => goImage('left', e)}
              className="absolute left-0 top-0 bottom-0 w-10 flex items-center justify-center bg-black/0 hover:bg-black/40 text-white/60 hover:text-white transition-colors z-[2]"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={(e) => goImage('right', e)}
              className="absolute right-0 top-0 bottom-0 w-10 flex items-center justify-center bg-black/0 hover:bg-black/40 text-white/60 hover:text-white transition-colors z-[2]"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 z-[2]">
              {images.map((_, i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === imgIdx ? 'bg-white' : 'bg-white/40'}`} />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="p-2 space-y-1">
        <div className="text-white font-medium text-sm truncate">{product.title}</div>
        {firstVariant && (
          <div className="flex items-center gap-2">
            <span className="text-cyan-400 font-semibold text-sm">
              {formatPrice(firstVariant.price)}
            </span>
            {firstVariant.compareAtPrice && parseFloat(firstVariant.compareAtPrice.amount) > parseFloat(firstVariant.price.amount) && (
              <span className="text-zinc-500 text-xs line-through">
                {formatPrice(firstVariant.compareAtPrice)}
              </span>
            )}
          </div>
        )}
        {desc && (
          <div className="text-zinc-400 text-xs line-clamp-2">
            {desc.length > 80 ? desc.slice(0, 80) + '...' : desc}
          </div>
        )}
        <div className="flex items-center justify-start gap-1.5 pt-1">
          <button
            onClick={handleAdd}
            disabled={adding || !canBuy}
            className={`px-4 py-2 text-sm rounded-full font-medium transition-colors flex items-center gap-1 ${
              !canBuy
                ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                : justAdded
                ? 'bg-green-600 text-white'
                : adding
                ? 'bg-cyan-700 text-cyan-200 cursor-wait'
                : 'bg-cyan-600 hover:bg-cyan-500 text-white'
            }`}
          >
            {adding ? (
              <div className="w-4 h-4 border-2 border-cyan-200 border-t-transparent rounded-full animate-spin" />
            ) : justAdded ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              !canBuy ? 'Sold Out' : (
                <>
                  +
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                </>
              )
            )}
          </button>
          <button
            onClick={onClick}
            className="px-4 py-2 text-sm rounded-full font-medium bg-white/10 hover:bg-white/20 text-zinc-300 transition-colors"
          >
            Learn More
          </button>
        </div>
      </div>
    </div>
  );
};

// Measure a box's live pixel width (the framed library viewers need real px).
function useBoxWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => { for (const e of entries) setW(e.contentRect.width); });
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// The ONE place a gallery item becomes an element — reused by the inline main
// view and the fullscreen lightbox. Images/videos render natively (video stays
// an autoplay/muted/looped turntable; +controls when full); every other
// supported type (3D model, pdf, audio, …) reuses the library block's shared
// <LibraryMediaView> so there's a single preview path for the whole app.
/** Sizing for the inline main image/video, derived from the site theme's shared
 *  product-header style. 'cover' fills the box (cropping, no letterbox bars);
 *  an aspect ratio fixes the gallery shape. Undefined → the classic layout. */
export interface GalleryMainStyle { fit: 'contain' | 'cover'; aspect?: string; maxH: number }

// The theme stores aspect as '4:3'; CSS wants '4 / 3'.
const mainMediaCss = (ms: GalleryMainStyle): React.CSSProperties => {
  const aspectRatio = ms.aspect ? ms.aspect.replace(':', ' / ') : undefined;
  if (ms.fit === 'cover') {
    return aspectRatio
      ? { width: '100%', aspectRatio, objectFit: 'cover', display: 'block' }
      : { width: '100%', height: ms.maxH, objectFit: 'cover', display: 'block' };
  }
  return { width: '100%', maxHeight: ms.maxH, objectFit: 'contain', display: 'block', ...(aspectRatio ? { aspectRatio } : null) };
};

// Callers MUST key this by `item.src` (see the call sites) — same reasoning
// as CardSlide's own key comment: this is a persistent, non-remounting
// instance that gets handed a different `item` on every thumbnail/Prev/Next
// click, and without a key a video swap would show one stale frame of the
// PREVIOUS item before the ref effect below catches up.
const GalleryMedia: React.FC<{ item: GalleryMediaItem; mode: 'main' | 'full'; title: string; mainStyle?: GalleryMainStyle }> = ({ item, mode, title, mainStyle }) => {
  const kind = galleryMediaKind(item);
  const [boxRef, boxW] = useBoxWidth();
  const full = mode === 'full';
  // Both called unconditionally (kind/mode can change across renders of the
  // SAME mounted instance — e.g. clicking a gallery thumbnail swaps `item`
  // in place) — each is a cheap no-op when its `small` is undefined, so no
  // cost for the kind that doesn't apply. The tiny plaintext placeholder
  // (free-CDN-servable, unlike the classic encrypted Library thumb) shows
  // instantly, then the small (~960px) tier shows almost instantly on a
  // thumbnail click instead of the multi-second wait for the full original;
  // the large/original tier only loads once the site-wide small-tier wave
  // settles.
  // Full/lightbox mode still ultimately ceilings at the TRUE original (a user
  // zooming in wants full quality, not the downscaled variant) — but it goes
  // through the same staged placeholder→small→large→original sequence to get
  // there, same as every other surface, instead of blocking on the full
  // original's full download on every single Prev/Next switch (that
  // regressed the lightbox back to the pre-progressive-system experience).
  // Plain Shopify-sourced items (no custom gallery — see CardSlide's own
  // comment on the same gap) never carry our variant fields, which used to
  // hit useProgressiveTierSrc's "nothing staged" fallback: original shown
  // immediately, unpreloaded. A genuinely tiny (24px) Shopify-resized
  // placeholder keeps this on the properly-gated path instead.
  // See CardSlide's identical comment: optimizeImageUrl no-ops for our own
  // media URLs, so guard against using that no-op as a "placeholder".
  const tinyShopifyPlaceholder = kind === 'image' ? optimizeImageUrl(item.src, 24) : item.src;
  const placeholder = item.variantPlaceholderSrc
    ?? (tinyShopifyPlaceholder !== item.src ? tinyShopifyPlaceholder : undefined);
  const galleryDisplayPx = full ? coverViewportPx() : 800;
  const imageSrc = useProgressiveTierSrc({
    placeholder, small: item.variantSrc, large: item.variantLgSrc,
    original: full ? item.src : optimizeImageUrl(item.src, 800),
    displayWidthPx: galleryDisplayPx,
    // Lightbox mode: continue past the ~1600px large tier to the true
    // original once it's rendered — a zoomed-in view is exactly the "might
    // be viewed large/close-up" case finalTier exists for.
    finalTier: full ? 'original' : 'large',
  });
  // Seamless small→original swap (resumes playback position, never reloads
  // mid-play) — see useProgressiveTierVideoRef.
  const videoRef = useProgressiveTierVideoRef({ small: item.videoVariantSrc, original: item.src, displayWidthPx: galleryDisplayPx });

  if (kind === 'image') {
    return full
      ? <img src={imageSrc} alt={item.altText || title} style={{ maxWidth: '92vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 8, display: 'block' }} />
      : mainStyle
        ? <img src={imageSrc} alt={item.altText || title} style={mainMediaCss(mainStyle)} />
        : <img src={imageSrc} alt={item.altText || title} className="w-full max-h-[400px] object-contain" />;
  }
  if (kind === 'video') {
    return (
      <video
        ref={videoRef}
        poster={item.posterVariantSrc || item.poster || thumbUrlFromMediaSrc(item.src)}
        autoPlay muted loop playsInline controls={full}
        className={full ? undefined : mainStyle ? 'bg-black' : 'w-full max-h-[400px] object-contain bg-black'}
        style={full ? { maxWidth: '92vw', maxHeight: '82vh', borderRadius: 8, background: '#000' } : mainStyle ? mainMediaCss(mainStyle) : undefined}
      />
    );
  }
  // 3D model / pdf / audio / other → the shared library preview.
  if (full) {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
    return <LibraryMediaView src={item.src} filename={item.filename || ''} mime={item.mime || ''} width={Math.min(Math.round(vw * 0.9), 1000)} height={Math.round(vh * 0.78)} autoRotate />;
  }
  const h = Math.round(Math.min(420, Math.max(260, (boxW || 360) * 0.6)));
  return (
    <div ref={boxRef} className="w-full">
      {(boxW > 0 || kind === 'audio') && (
        <LibraryMediaView src={item.src} filename={item.filename || ''} mime={item.mime || ''} width={boxW || 360} height={h} autoRotate />
      )}
    </div>
  );
};

// Fullscreen gallery preview, reusing the library-block lightbox look (dark
// backdrop, centered media, click-out / Esc to close, ‹ › to step). Renders the
// active item through the shared <GalleryMedia> so every file type previews the
// same way it does in the library block.
const GalleryLightbox: React.FC<{
  items: GalleryMediaItem[];
  index: number;
  title: string;
  onIndex: (i: number) => void;
  onClose: () => void;
}> = ({ items, index, title, onIndex, onClose }) => {
  const count = items.length;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!pageMayTakeKey(e)) return;   // a text field's caret keys are its own
      if (e.key === 'Escape') onClose();
      else if (count > 1 && e.key === 'ArrowRight') onIndex((index + 1) % count);
      else if (count > 1 && e.key === 'ArrowLeft') onIndex((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onIndex, onClose]);

  const item = items[index];
  if (!item) return null;
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.9)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '100%', maxHeight: '100%' }}>
        <GalleryMedia key={item.src} item={item} mode="full" title={title} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 12, color: '#e4e4e7' }}>
          {count > 1 && (
            <>
              <button type="button" onClick={() => onIndex((index - 1 + count) % count)}
                style={{ fontSize: 14, color: '#e4e4e7', background: 'rgba(255,255,255,0.12)', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer' }}>‹ Prev</button>
              <span style={{ fontSize: 13, opacity: 0.8 }}>{index + 1} / {count}</span>
              <button type="button" onClick={() => onIndex((index + 1) % count)}
                style={{ fontSize: 14, color: '#e4e4e7', background: 'rgba(255,255,255,0.12)', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer' }}>Next ›</button>
            </>
          )}
          <button type="button" onClick={onClose}
            style={{ fontSize: 14, color: '#fff', background: '#2563eb', border: 'none', padding: '6px 16px', borderRadius: 6, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

// =====================
// Product Detail Page
// =====================
export const ProductDetail: React.FC<{
  product: StoreProduct;
  onBack: () => void;
  onAddToCart: (variantId: string) => Promise<void>;
  isMobile: boolean;
  /** Show the desktop "Back to Store" bar. True for the in-chat mini-store; the
   *  site builder renders product pages without it (the back bar is meaningless
   *  there — the page is reached via the site nav). */
  showBack?: boolean;
  /** Detail layout style (site builder). 'stacked' (default) preserves the
   *  in-chat mini-store look; the two-column variants collapse to stacked on
   *  mobile. */
  layout?: 'stacked' | 'gallery-left' | 'gallery-right';
  /** Gallery media to render. Defaults to the live Shopify product images; the
   *  site builder passes a merged list (Shopify + our own bucket media). Each
   *  item's mime/filename pick the renderer (image / autoplay-video / 3D / pdf…). */
  galleryImages?: { src: string; altText: string | null; mime?: string; filename?: string; poster?: string | null }[];
  /** Editor-only: when provided, REPLACE the static title / description with an
   *  inline editor node (the site builder passes a <TextSubblock>). Keeps this
   *  shared component editor-free — the editor node is constructed by the caller,
   *  so the public render path never imports it. */
  titleSlot?: React.ReactNode;
  descriptionSlot?: React.ReactNode;
  /** Editor-only: inline editor for the site-wide disclaimer shown above the
   *  Add-to-Cart button (replaces the static headerStyle.disclaimerHtml render). */
  disclaimerSlot?: React.ReactNode;
  /** Per-variant hero image: variant id → index into `galleryImages`. Selecting
   *  that variant advances the gallery's main image to it (site builder). */
  variantHeroIndex?: Record<string, number>;
  /** Auto-advance the gallery like a slideshow every `autoAdvanceSeconds`. */
  autoAdvance?: boolean;
  autoAdvanceSeconds?: number;
  /** Background color for the detail surface + image letterbox area. '' or
   *  undefined keeps the default dark theme. */
  bgColor?: string;
  /** Site-wide product-header style (padding, type sizes, add-to-cart button,
   *  gallery image fit) from the site theme — see themeProductHeaderStyle.
   *  Undefined (the in-chat mini-store) keeps the classic look. */
  headerStyle?: ProductHeaderStyle;
}> = ({ product, onBack, onAddToCart, isMobile, showBack = true, layout = 'stacked', galleryImages, titleSlot, descriptionSlot, disclaimerSlot, variantHeroIndex, autoAdvance, autoAdvanceSeconds, bgColor, headerStyle }) => {
  const [mainImageIdx, setMainImageIdx] = useState(0);
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const [adding, setAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  // Auto-advance pauses once the shopper manually picks an image, and resumes
  // when they next select a variant.
  const [autoPaused, setAutoPaused] = useState(false);
  // Fullscreen gallery lightbox (null = closed). Lets shoppers inspect a media
  // item close up; reuses the library-block preview overlay style.
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  // The gallery source: caller-supplied merged list (site builder) or, by
  // default, the product's live Shopify images (in-chat mini-store). Typed as
  // GalleryMediaItem so the optional media fields (mime/filename/poster) are available.
  const images: GalleryMediaItem[] = galleryImages ?? product.images;
  const safeIdx = Math.min(mainImageIdx, Math.max(0, images.length - 1));

  const variant = product.variants[selectedVariantIdx];
  const hasMultipleVariants = product.variants.length > 1 && product.variants.some((v) => v.title !== 'Default Title');

  // When the shopper picks a variant that has a designated hero image, advance
  // the gallery to it (site builder feature). Manual thumbnail clicks still win
  // until the next variant change.
  useEffect(() => {
    const idx = variant ? variantHeroIndex?.[variant.id] : undefined;
    if (idx != null && idx >= 0) setMainImageIdx(idx);
  }, [selectedVariantIdx, variant, variantHeroIndex]);

  // Gallery slideshow: cycle the main image on a timer when enabled (≥2 images).
  // Pauses while the shopper is manually viewing an image (autoPaused).
  useEffect(() => {
    if (!autoAdvance || autoPaused || images.length <= 1) return;
    const ms = Math.max(1, autoAdvanceSeconds ?? 4) * 1000;
    const t = setInterval(() => setMainImageIdx((i) => (i + 1) % images.length), ms);
    return () => clearInterval(t);
  }, [autoAdvance, autoPaused, autoAdvanceSeconds, images.length]);

  const canBuy = variant?.availableForSale || isDigital(product);

  const handleAdd = useCallback(async () => {
    if (!variant || !canBuy) return;
    setAdding(true);
    try {
      await onAddToCart(variant.id);
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1500);
    } finally {
      setAdding(false);
    }
  }, [variant, canBuy, onAddToCart]);

  // Gallery (main image + thumbnail strip) and the buy box (title/price/variants/
  // CTA/description) are factored out so the layout variants can arrange them
  // side-by-side without duplicating markup. `stacked` concatenates them exactly
  // as the in-chat mini-store always has.
  const mainItem = images[safeIdx];
  const mainKind = mainItem ? galleryMediaKind(mainItem) : 'image';
  // Image/video main views open the lightbox on click. Framed types (3D/pdf/
  // audio) are interactive inline (orbit/scroll/play), so they instead get a
  // small "expand" button — clicking the media itself must not hijack orbit.
  const mainClickable = mainKind === 'image' || mainKind === 'video';
  const hs = headerStyle;
  const mainStyle: GalleryMainStyle | undefined = hs
    ? { fit: hs.imageFit, aspect: hs.imageAspect || undefined, maxH: hs.imageMaxH }
    : undefined;
  const renderGallery = () => (
    <>
      {images.length > 0 && mainItem && (
        <div className="relative bg-zinc-900" style={bgColor ? { backgroundColor: bgColor } : undefined}>
          {mainClickable ? (
            <button type="button" onClick={() => setLightboxIdx(safeIdx)} title="Click to view fullscreen" className="block w-full cursor-zoom-in">
              <GalleryMedia key={mainItem.src} item={mainItem} mode="main" title={product.title} mainStyle={mainStyle} />
            </button>
          ) : (
            <>
              <GalleryMedia key={mainItem.src} item={mainItem} mode="main" title={product.title} mainStyle={mainStyle} />
              <button type="button" onClick={() => setLightboxIdx(safeIdx)} title="View fullscreen"
                className="absolute top-2 right-2 z-10 rounded bg-black/60 px-2 py-1 text-[11px] text-white hover:bg-black/80">⤢</button>
            </>
          )}
        </div>
      )}

      {images.length > 1 && (
        <div className="flex gap-1.5 px-3 py-2 overflow-x-auto scrollbar-hide">
          {images.map((img, i) => {
            const k = galleryMediaKind(img);
            return (
              <button
                key={img.src + i}
                onClick={() => { setMainImageIdx(i); setAutoPaused(true); }}
                className={`relative flex-shrink-0 w-14 h-14 rounded overflow-hidden border-2 transition-colors ${
                  i === safeIdx ? 'border-cyan-500' : 'border-transparent hover:border-white/30'
                }`}
              >
                {k === 'image' ? (
                  <GalleryStripThumb src={img.src} variantSrc={img.variantSrc} alt={img.altText || `${product.title} ${i + 1}`} className="w-full h-full object-cover" />
                ) : k === 'video' ? (
                  <>
                    {(() => {
                      const still = img.poster || thumbUrlFromMediaSrc(img.src);
                      return still ? (
                        <img src={still} alt={img.altText || `${product.title} ${i + 1}`} className="w-full h-full object-cover bg-black" />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center bg-zinc-800 text-lg">🎬</span>
                      );
                    })()}
                    <span className="absolute bottom-0.5 right-0.5 text-[9px] leading-none px-0.5 rounded bg-black/70 text-white">▶</span>
                  </>
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-zinc-800 text-lg" title={k}>{GALLERY_KIND_GLYPH[k] || '📁'}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {lightboxIdx != null && images[lightboxIdx] && (
        <GalleryLightbox
          items={images}
          index={lightboxIdx}
          title={product.title}
          onIndex={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  );

  const hasDisclaimer = !!(disclaimerSlot || (hs?.disclaimerHtml || '').trim());
  const renderBuyBox = () => (
    <div className="px-3 py-3 space-y-4">
      {/* pointerEvents:auto lets the inline editor receive clicks even though the
          builder renders the whole ProductDetail inside a pointer-events:none
          wrapper (so cart/thumbnails stay inert). */}
      <h2 className="text-lg font-semibold text-white" style={{ ...(titleSlot ? { pointerEvents: 'auto' as const } : null), ...(hs ? { fontSize: hs.titleSize } : null) }}>
        {titleSlot ?? product.title}
      </h2>

      <div className="flex items-center gap-3">
        {variant && (
          <>
            <span className="text-cyan-400 font-bold text-xl" style={hs ? { fontSize: hs.priceSize } : undefined}>
              {formatPrice(variant.price)}
            </span>
            {variant.compareAtPrice && parseFloat(variant.compareAtPrice.amount) > parseFloat(variant.price.amount) && (
              <span className="text-zinc-500 text-base line-through">
                {formatPrice(variant.compareAtPrice)}
              </span>
            )}
          </>
        )}
      </div>

      {/* Site-wide disclaimer (import/shipping notes etc. — same rich-HTML
          snippet on every product page) grouped tightly with the variant picker,
          above the Add-to-Cart button. The editor swaps in an inline editor via
          disclaimerSlot. */}
      {(hasDisclaimer || hasMultipleVariants) && (
        <div className="space-y-1.5">
          {hasDisclaimer && (
            <div className="store-description text-xs text-zinc-400 leading-relaxed" style={disclaimerSlot ? { pointerEvents: 'auto' } : undefined}>
              {disclaimerSlot ?? <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(hs!.disclaimerHtml) }} />}
            </div>
          )}
          {hasMultipleVariants && (
            <div className="space-y-1.5">
              <label className="text-xs text-zinc-400 font-medium">Variant</label>
              <div className="flex flex-wrap gap-1.5">
                {product.variants.map((v, i) => (
                  <button
                    key={v.id}
                    onClick={() => { setSelectedVariantIdx(i); setAutoPaused(false); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      i === selectedVariantIdx
                        ? 'bg-cyan-600 text-white'
                        : 'bg-white/10 text-zinc-300 hover:bg-white/20'
                    } ${!v.availableForSale ? 'opacity-50 line-through' : ''}`}
                  >
                    {v.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add to Cart button. Theme overrides apply only to the idle buyable
          state — the sold-out / adding / added state colors stay as-is so the
          feedback states remain readable on any custom color. */}
      <button
        onClick={handleAdd}
        disabled={adding || !canBuy}
        className={`w-full py-3 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
          !canBuy
            ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
            : justAdded
            ? 'bg-green-600 text-white'
            : adding
            ? 'bg-cyan-700 text-cyan-200 cursor-wait'
            : 'bg-cyan-600 hover:bg-cyan-500 text-white'
        } ${hs?.btnBgColor && canBuy && !adding && !justAdded ? 'store-atc-custom' : ''}`}
        style={hs ? {
          fontSize: hs.btnFontSize,
          borderRadius: hs.btnRadius,
          paddingTop: hs.btnPaddingY,
          paddingBottom: hs.btnPaddingY,
          ...(canBuy && !adding && !justAdded && hs.btnBgColor ? { backgroundColor: hs.btnBgColor } : null),
          ...(canBuy && !adding && !justAdded && hs.btnTextColor ? { color: hs.btnTextColor } : null),
        } : undefined}
      >
        {adding ? (
          <div className="w-4 h-4 border-2 border-cyan-200 border-t-transparent rounded-full animate-spin" />
        ) : !canBuy ? (
          'Sold Out'
        ) : justAdded ? (
          <>
            Added
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </>
        ) : (
          <>
            {hs?.btnLabel.trim() || 'Add to Cart'}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
          </>
        )}
      </button>

      {(descriptionSlot || product.descriptionHtml) && (
        <div className="border-t border-white/10 pt-4">
          {descriptionSlot ? (
            <div className="store-description text-sm text-zinc-300 leading-relaxed" style={{ pointerEvents: 'auto', ...(hs ? { fontSize: hs.descSize } : null) }}>
              {descriptionSlot}
            </div>
          ) : (
            <div
              className="store-description text-sm text-zinc-300 leading-relaxed"
              style={hs ? { fontSize: hs.descSize } : undefined}
              dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(product.descriptionHtml) }}
            />
          )}
        </div>
      )}
    </div>
  );

  // Two-column layouts apply only on a real desktop viewport; mobile always
  // collapses to the simple stacked layout.
  const twoColumn = !isMobile && (layout === 'gallery-left' || layout === 'gallery-right');

  return (
    <div className="flex flex-col h-full bg-zinc-950/90" style={bgColor ? { backgroundColor: bgColor } : undefined}>
      {/* Back button — desktop only */}
      {showBack && !isMobile && (
        <div className="flex items-center gap-2 p-3 border-b border-white/10 flex-shrink-0">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-zinc-300 hover:text-white transition-colors text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Store
          </button>
        </div>
      )}

      {/* Scrollable content. overflow-x-clip keeps the header exactly its own
          width so it can never scroll sideways (e.g. from sub-pixel column
          rounding); clip doesn't create a scroll container, so the sticky buy box
          still works. */}
      <div className="flex-1 overflow-y-auto overflow-x-clip" style={hs ? { padding: `${hs.paddingY}px ${hs.paddingX}px` } : undefined}>
        {twoColumn ? (
          // Proportional flex (basis 0, grow 55/45) shares the space that REMAINS
          // after the gap, so the two columns total exactly 100% — fixed 55%/45%
          // widths plus the gap overshot the row and forced a horizontal scrollbar.
          // min-w-0 lets a wide gallery image shrink instead of pushing the row wider.
          <div className={`flex gap-6 px-3 py-3 ${layout === 'gallery-right' ? 'flex-row-reverse' : ''}`}>
            <div className="min-w-0" style={{ flex: '55 1 0%' }}>{renderGallery()}</div>
            <div className="min-w-0 self-start sticky top-3" style={{ flex: '45 1 0%' }}>{renderBuyBox()}</div>
          </div>
        ) : (
          <>
            {renderGallery()}
            {renderBuyBox()}
          </>
        )}
      </div>

      <style>{`
        .store-description h1, .store-description h2, .store-description h3 {
          color: white; font-weight: 600; margin-top: 1em; margin-bottom: 0.5em;
        }
        .store-description h1 { font-size: 1.25rem; }
        .store-description h2 { font-size: 1.1rem; }
        .store-description h3 { font-size: 1rem; }
        .store-description p { margin-bottom: 0.75em; }
        .store-description ul, .store-description ol { padding-left: 1.5em; margin-bottom: 0.75em; }
        .store-description ul { list-style-type: disc; }
        .store-description ol { list-style-type: decimal; }
        .store-description li { margin-bottom: 0.25em; }
        .store-description a { color: rgb(34 211 238); text-decoration: underline; }
        .store-description a:hover { color: rgb(103 232 249); }
        .store-description img { max-width: 100%; border-radius: 0.5rem; margin: 0.5em 0; }
        .store-description strong, .store-description b { color: white; }
        /* Hover for a theme-colored add-to-cart: the inline background wins over
           the tailwind hover class, so brighten it instead. */
        .store-atc-custom:hover:not(:disabled) { filter: brightness(1.15); }
      `}</style>
    </div>
  );
};
